import classNames from 'classnames';
import { formatISO } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import { Field, useFormikContext } from 'formik';
import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { LOADING_TYPES, useCheckoutState } from '../../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../../utils/config';
import getValidator from '../../../../utils/getValidator';
import { useModal } from '../../../../utils/modal';
import Button from '../../../atoms/Button';
import LoadingIndicator from '../../../atoms/LoadingIndicator';
import TextField from '../../../formik/TextField';
import './style.scss';

const reducedDateFnsLocales = {
  cs: dateFnsLocales.cs,
  de: dateFnsLocales.de,
  en: dateFnsLocales.enUS,
  fr: dateFnsLocales.fr,
  nl: dateFnsLocales.nl,
  sv: dateFnsLocales.sv
};

type DayPickerFormProps = {
  max: Date;
  min: Date;
  onClose: () => void;
  onSubmit: (value: Date) => Promise<void>;
};

const DayPickerForm = ({ max, min, onClose, onSubmit }: DayPickerFormProps) => {
  const { checkoutState, isLoading } = useCheckoutState();
  const { orderState } = checkoutState;
  const dateFromState = orderState.shipping?.selected?.[0].deliveryDate;

  const [datePickerDate, setDatePickerDate] = useState<Date | undefined>(
    dateFromState ? new Date(dateFromState) : undefined
  );

  return (
    <div>
      <DayPicker
        mode="single"
        locale={reducedDateFnsLocales[config.locale.substring(0, 2)]}
        disabled={(date: Date) => date.getDay() === 0}
        selected={datePickerDate}
        onSelect={(value?: Date) => value && setDatePickerDate(value)}
        fromDate={min}
        toDate={max}
      />
      <div className="scheduled-delivery__buttons">
        <Button secondary type="button" onClick={onClose}>
          <FormattedMessage id="generic.cancel" />
        </Button>
        <Button
          type="button"
          onClick={() => datePickerDate && onSubmit(datePickerDate)}
          isLoading={!!isLoading}
          disabled={!datePickerDate}
        >
          <FormattedMessage id="generic.select" />
        </Button>
      </div>
    </div>
  );
};

const ScheduledDeliveryForm = () => {
  const { checkoutState, setCarrierAndShippingOption, isLoading, setLoading } = useCheckoutState();
  const { orderState } = checkoutState;
  const { values, setFieldValue } = useFormikContext<any>();
  const { formatMessage } = useIntl();
  const { toggleModal } = useModal();

  const selectedCarrier = orderState.shipping?.selected?.[0];
  const isCorrectOption =
    selectedCarrier?.carrierGroupKey === values.carrier.carrierGroupKey &&
    selectedCarrier?.shippingOptionKey === 'scheduled_delivery';

  if (!selectedCarrier || !isCorrectOption) {
    return <></>;
  }

  const { carrierKey, carrierGroupKey, deliveryDates, shippingOptionKey } = selectedCarrier;
  const scheduledLoadingValue = `${carrierGroupKey}-${shippingOptionKey}`;

  const onClose = () => toggleModal();
  const onSubmit = async (datePickerDate: Date) => {
    const formattedDate = formatISO(datePickerDate!, { representation: 'date' });
    try {
      setLoading(LOADING_TYPES.carrierChange(scheduledLoadingValue));
      await setCarrierAndShippingOption({
        data: { carrierKey, carrierGroupKey, deliveryOptionKey: 'home_delivery', shippingOptionKey },
        specification: { deliveryDate: formattedDate }
      });
      setFieldValue('carrier.deliveryDate', formattedDate);
    } catch (err) {
      console.log(err);
    }
    onClose();
    setLoading(LOADING_TYPES.off);
  };

  const min = new Date(deliveryDates!.minimum);
  const max = new Date(deliveryDates!.maximum);

  const modalConfig = {
    children: <DayPickerForm onClose={onClose} onSubmit={onSubmit} min={min} max={max} />,
    contentClassName: 'scheduled-delivery__modal',
    backgroundClassName: 'scheduled-delivery__modal-background',
    onEscapeKey: () => toggleModal(),
    onClickOutside: () => toggleModal()
  };

  const showModal = () => {
    toggleModal(modalConfig);
  };

  return (
    <div className="scheduled-delivery">
      <div
        className={classNames('scheduled-delivery__btn', {
          'cursor-disabled': isLoading === LOADING_TYPES.carrierChange(scheduledLoadingValue),
          'scheduled-delivery__btn--disabled':
            !!isLoading && isLoading !== LOADING_TYPES.carrierChange(scheduledLoadingValue)
        })}
        onClick={() => !isLoading && showModal()}
      >
        <img src={getAssetUrl('common/calendar.svg')} alt="calendar" />
        {isLoading === LOADING_TYPES.carrierChange(scheduledLoadingValue) ?
          <LoadingIndicator isSmall contentClassName="scheduled-delivery__loading" />
        : values.carrier.deliveryDate ?
          <FormattedDate value={new Date(values.carrier.deliveryDate)} year="numeric" month="long" day="numeric" />
        : <FormattedMessage id="shipping.carrier.scheduledDeliveryForm.select" />}
      </div>
      <Field
        name="carrier.deliveryDate"
        label={formatMessage({ id: 'shipping.carrier.scheduledDeliveryForm.deliveryDate' })}
        className="scheduled-delivery__invisible-field"
        component={TextField}
        validate={getValidator({ required: true })}
      />
    </div>
  );
};

export default ScheduledDeliveryForm;
